import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, of, BehaviorSubject, pipe } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { ResponseWrapper } from '@library/common/http';

import { CacheService } from '@library/core/services';

import { Report, ReportColumn } from './report';
import { reportGroup } from './report.group';

@Injectable({
   providedIn: 'root'
})
export class ReportDetailsService {

   constructor(
      private http: HttpClient,
      private cache: CacheService,
      private snackbar: MatSnackBar,
   ) { }

   url = `report/manager`;

   report$: BehaviorSubject<Report> = new BehaviorSubject( null );
   reportId$: BehaviorSubject<any> = new BehaviorSubject( null );

   _reportForm: FormGroup = reportGroup();
   get reportForm() { return this._reportForm; }
   set reportForm( group: FormGroup ) {
      this._reportForm = group;
      this.reportForm.valueChanges.subscribe( report =>
         this.report$.next({ ...this.report$.getValue(), ...report }));
   }

   get columns(): Observable<ReportColumn[]> {
      return this.cache.request<ResponseWrapper<ReportColumn>, ReportColumn[]>({
         url: `enum/report_columns`,
         pipe: pipe(
            map( rw => rw.response.collection ),
            map( columns => columns.map( c => ({ ...c, type: c.type.toLowerCase() }))),
            catchError( _ => [] ),
         ),
         useLambda: true,
      });
   }

   load( id: number ): Observable<Report> {
      return this.cache.request<ResponseWrapper<Report>, Report>({
         url: `${this.url}/${id}`,
         useLambda: true,
         pipe: pipe(
            map( rw => rw.response.collection[0] ),
            map( report => new Report( report )),
            tap( report => this.reportForm = reportGroup( report )),
            tap( report => this.report$.next( report )),
         ),
      });
   }

   refresh( id: number ): Observable<Report> {
      if ( !id ) { return; }

      this.reset();
      this.cache.clearSet( this.url )

      return this.load( id ).pipe();
   }

   copy( id: number ): Observable<Report> {
      if ( !id ) { return; }

      return this.load( id ).pipe(
         map( report => new Report({ ...report, name: `Copy of ${report.name}`, id: null })),
         tap( report => this.reportForm = reportGroup( report )),
         tap( report => this.report$.next( report )),
      );
   }

   delete( id: number ): Observable<boolean> {
      if ( !id ) { return of( false ); }

      return this.http.delete<ResponseWrapper>( this.url + '/' + id,  { params: { useLambda: true } } ).pipe(
         map( rw => !rw.messages.length ),
         tap( _ => this.reset() ),
         tap( _ => this.cache.clearSet( this.url )),
      );
   }

   save(): Observable<boolean> {
      if ( this.reportForm.invalid ) { return; }

      const payload = this.reportForm.value;

      const savePipe = pipe(
         tap(( rw: ResponseWrapper ) => this.reportId$.next(rw.response.collection[0]?.id)),
         map(( rw: ResponseWrapper ) => rw && rw.messages && !rw.messages.length ),
         tap( _ => this.cache.clearSet( this.url )),
         tap( _ => this.reset() ),
         tap( _ => this.snackbar.open( 'Saved!', null, { duration: 1000, verticalPosition: 'bottom' })),
      );

      if ( payload?.id ) {
         return this.http.patch<ResponseWrapper>( this.url, payload,  { params: { useLambda: true } } ).pipe( savePipe );
      }

      return this.http.post<ResponseWrapper>( this.url, payload,  { params: { useLambda: true } } ).pipe( savePipe );
   }

   reset() {
      this.report$.next( null );
      this._reportForm = reportGroup();
   }
}
